import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, DollarSign, MapPin, Calendar, Clock, Gift, TrendingUp, Briefcase, Loader2, ChevronDown, ChevronUp, Trophy, AlertCircle } from 'lucide-react';

const PRIORITY_OPTIONS = [
  'Total Compensation',
  'Work-Life Balance',
  'Growth Potential',
  'Location & Remote',
  'Company Culture',
  'Benefits & PTO',
  'Job Responsibilities',
  'Team & Manager',
];

function OfferComparisonView({ authToken, onNavigate }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [showPriorities, setShowPriorities] = useState(false);

  useEffect(() => {
    if (authToken) {
      fetch('/api/user/offers', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
        .then(res => res.ok ? res.json() : { offers: [] })
        .then(data => setOffers(data.offers || []))
        .catch((error) => {
          console.error('Error loading offers:', error);
          if (window.posthog) {
            window.posthog.captureException(error, { component: 'OfferComparisonView', action: 'loadOffers' });
          }
          setOffers([]);
        })
        .finally(() => setLoading(false));
    }
  }, [authToken]);

  const handleCompare = async () => {
    setComparing(true);
    try {
      const response = await fetch('/api/user/offers/compare', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ priorities: selectedPriorities })
      });
      if (response.ok) {
        const data = await response.json();
        setComparison(data.comparison);
      }
    } catch (error) {
      console.error('Error comparing offers:', error);
      if (window.posthog) {
        window.posthog.captureException(error, { component: 'OfferComparisonView', action: 'compareOffers' });
      }
    } finally {
      setComparing(false);
    }
  };

  const togglePriority = (priority) => {
    setSelectedPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const formatCurrency = (val) => {
    if (!val) return '-';
    const num = typeof val === 'string' ? parseInt(val, 10) : val;
    if (isNaN(num)) return '-';
    return '$' + num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="animate-fadeIn flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
        <span className="ml-2 text-slate-500">Loading offers...</span>
      </div>
    );
  }

  if (offers.length < 2) {
    return (
      <div className="animate-fadeIn">
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors cursor-pointer mb-6"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Trophy size={32} className="text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Need More Offers to Compare
          </h2>
          <p className="text-slate-600 max-w-md mx-auto">
            You need at least 2 offers to use the comparison tool. Set an application's status to "Offer" and add details to get started.
          </p>
          <button
            onClick={() => onNavigate('library', 'offer')}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-dark transition-colors cursor-pointer"
          >
            View Offer Applications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors cursor-pointer mb-2"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Trophy size={24} className="text-green-600" />
            Compare Offers
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {offers.length} offer{offers.length !== 1 ? 's' : ''} to compare
          </p>
        </div>
      </div>

      {/* Offer Cards Grid */}
      <div className={`grid gap-4 ${offers.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {offers.map((offer, idx) => (
          <OfferCard
            key={offer.id}
            offer={offer}
            index={idx}
            formatCurrency={formatCurrency}
            isRecommended={comparison?.recommendation?.includes(offer.company)}
          />
        ))}
      </div>

      {/* Priority Picker */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <button
          onClick={() => setShowPriorities(!showPriorities)}
          className="flex items-center justify-between w-full cursor-pointer"
        >
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles size={18} className="text-primary" />
            Set Your Priorities
            {selectedPriorities.length > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {selectedPriorities.length} selected
              </span>
            )}
          </h3>
          {showPriorities ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>

        {showPriorities && (
          <div className="mt-4">
            <p className="text-sm text-slate-500 mb-3">Select what matters most to you. AI will consider these when comparing.</p>
            <div className="flex flex-wrap gap-2">
              {PRIORITY_OPTIONS.map((priority) => (
                <button
                  key={priority}
                  onClick={() => togglePriority(priority)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-all cursor-pointer ${
                    selectedPriorities.includes(priority)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary'
                  }`}
                >
                  {selectedPriorities.includes(priority) && (
                    <span className="mr-1">{selectedPriorities.indexOf(priority) + 1}.</span>
                  )}
                  {priority}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Compare Button */}
      <div className="flex justify-center">
        <button
          onClick={handleCompare}
          disabled={comparing}
          className={`inline-flex items-center gap-2 px-8 py-3 font-medium rounded-lg transition-colors ${
            comparing ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-dark cursor-pointer'
          }`}
        >
          {comparing ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Analyzing Offers...
            </>
          ) : (
            <>
              <Sparkles size={20} />
              Compare with AI
            </>
          )}
        </button>
      </div>

      {/* AI Comparison Results */}
      {comparison && (
        <ComparisonResults comparison={comparison} formatCurrency={formatCurrency} />
      )}
    </div>
  );
}

function OfferCard({ offer, index, formatCurrency, isRecommended }) {
  return (
    <div className={`rounded-lg border p-5 relative ${isRecommended ? 'border-green-500 bg-green-50/50' : 'border-slate-200 bg-white'}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-4 px-3 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
          Recommended
        </div>
      )}

      <div className="mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900">{offer.company}</h3>
        <p className="text-sm text-slate-600">{offer.role}</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500 flex items-center gap-1.5">
            <DollarSign size={14} /> Base
          </span>
          <span className="font-semibold text-slate-900">{formatCurrency(offer.salary_base)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500 flex items-center gap-1.5">
            <TrendingUp size={14} /> Bonus
          </span>
          <span className="font-semibold text-slate-900">{formatCurrency(offer.salary_bonus)}</span>
        </div>
        {offer.signing_bonus && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500 flex items-center gap-1.5">
              <Gift size={14} /> Signing
            </span>
            <span className="font-semibold text-slate-900">{formatCurrency(offer.signing_bonus)}</span>
          </div>
        )}
        {offer.equity && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500 flex items-center gap-1.5">
              <Briefcase size={14} /> Equity
            </span>
            <span className="font-semibold text-slate-900 text-right text-xs max-w-[60%] truncate">{offer.equity}</span>
          </div>
        )}

        <div className="border-t border-slate-200 pt-3 mt-3 space-y-2">
          {offer.location && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <MapPin size={14} className="text-slate-400" />
              {offer.location} {offer.remote_policy && `(${offer.remote_policy})`}
            </div>
          )}
          {offer.pto_days && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <Clock size={14} className="text-slate-400" />
              {offer.pto_days} days PTO
            </div>
          )}
          {offer.start_date && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <Calendar size={14} className="text-slate-400" />
              Start: {new Date(offer.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ComparisonResults({ comparison, formatCurrency }) {
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Sparkles size={20} className="text-primary" />
          AI Analysis
        </h3>
        {comparison.summary && (
          <p className="text-slate-700 leading-relaxed mb-4">{comparison.summary}</p>
        )}
        {comparison.recommendation && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-1">
              <Trophy size={16} />
              Recommendation
            </h4>
            <p className="text-green-700 text-sm">{comparison.recommendation}</p>
          </div>
        )}
      </div>

      {/* Per-offer Pros & Cons */}
      {comparison.offers && comparison.offers.length > 0 && (
        <div className={`grid gap-4 ${comparison.offers.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {comparison.offers.map((offer, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading font-bold text-slate-900">{offer.company}</h4>
                {offer.rating && (
                  <span className={`px-2 py-0.5 text-sm font-bold rounded-full ${
                    offer.rating >= 8 ? 'bg-green-100 text-green-700' :
                    offer.rating >= 6 ? 'bg-slate-100 text-slate-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {offer.rating}/10
                  </span>
                )}
              </div>
              {offer.totalAnnualComp && (
                <p className="text-sm text-slate-500 mb-3">
                  Est. Total Comp: <span className="font-semibold text-slate-900">{formatCurrency(offer.totalAnnualComp)}/yr</span>
                </p>
              )}
              {offer.pros && offer.pros.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Pros</p>
                  <ul className="space-y-1">
                    {offer.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                        <span className="text-green-500 mt-0.5">+</span>
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {offer.cons && offer.cons.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Cons</p>
                  <ul className="space-y-1">
                    {offer.cons.map((con, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                        <span className="text-red-500 mt-0.5">-</span>
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Category Comparison */}
      {comparison.comparisonCategories && comparison.comparisonCategories.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Category Breakdown</h3>
          <div className="space-y-3">
            {comparison.comparisonCategories.map((cat, idx) => (
              <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-2 border-b border-slate-100 last:border-0">
                <div className="sm:w-40 font-medium text-slate-700 text-sm">{cat.category}</div>
                <div className="flex-1">
                  <span className="text-sm font-semibold text-primary">{cat.winner}</span>
                  {cat.details && (
                    <span className="text-sm text-slate-500 ml-2">- {cat.details}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Negotiation Tips */}
      {comparison.negotiationTips && comparison.negotiationTips.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2 mb-3">
            <AlertCircle size={18} className="text-slate-600" />
            Negotiation Tips
          </h3>
          <ul className="space-y-2">
            {comparison.negotiationTips.map((tip, idx) => (
              <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                <span className="font-medium text-slate-500 flex-shrink-0">{idx + 1}.</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default OfferComparisonView;
